import React, { useEffect, useMemo, useRef, useState } from 'react';
import Navbar from './Navbar';

const API_BASE = 'https://api.insightos.co.uk';

function formatBytes(bytes) {
	const value = Number(bytes || 0);
	if (!Number.isFinite(value) || value <= 0) return '0 B';
	const units = ['B', 'KB', 'MB', 'GB'];
	let i = 0;
	let v = value;
	while (v >= 1024 && i < units.length - 1) {
		v /= 1024;
		i += 1;
	}
	return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export default function Advert() {
	const [prompt, setPrompt] = useState('');
	const [duration, setDuration] = useState('');
	const [referenceFiles, setReferenceFiles] = useState([]);

	const [jobId, setJobId] = useState('');
	const [status, setStatus] = useState('idle');
	const [progress, setProgress] = useState(0);
	const [videoUrl, setVideoUrl] = useState('');
	const [downloadUrl, setDownloadUrl] = useState('');

	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState('');

	const pollTimerRef = useRef(null);

	const previews = useMemo(() => {
		return referenceFiles.map((file) => ({
			file,
			url: URL.createObjectURL(file),
			isVideo: String(file.type || '').toLowerCase().startsWith('video/'),
			isImage: String(file.type || '').toLowerCase().startsWith('image/'),
		}));
	}, [referenceFiles]);

	useEffect(() => {
		return () => {
			previews.forEach((p) => URL.revokeObjectURL(p.url));
		};
	}, [previews]);

	const resetJob = () => {
		setJobId('');
		setStatus('idle');
		setProgress(0);
		setVideoUrl('');
		setDownloadUrl('');
	};

	const stopPolling = () => {
		if (pollTimerRef.current) {
			clearInterval(pollTimerRef.current);
			pollTimerRef.current = null;
		}
	};

	const pollStatus = async (targetJobId) => {
		if (!targetJobId) return;
		try {
			const response = await fetch(`${API_BASE}/sora/prompt/${encodeURIComponent(targetJobId)}`, {
				method: 'GET',
				credentials: 'include',
			});
			const data = await response.json();
			if (!response.ok) {
				throw new Error(data?.message || 'Failed to poll video status');
			}

			setStatus(data.status || 'processing');
			setProgress(Number(data.progress ?? 0));

			if (data.status === 'completed') {
				const resolvedVideoUrl = data.videoUrl
					? (String(data.videoUrl).startsWith('http') ? data.videoUrl : `${API_BASE}${data.videoUrl}`)
					: '';
				const resolvedDownloadUrl = data.downloadUrl
					? (String(data.downloadUrl).startsWith('http') ? data.downloadUrl : `${API_BASE}${data.downloadUrl}`)
					: `${API_BASE}/sora/prompt/${targetJobId}/download`;
				setVideoUrl(resolvedVideoUrl);
				setDownloadUrl(resolvedDownloadUrl);
				stopPolling();
			}
		} catch (err) {
			setError(err?.message || 'Error polling video job.');
			stopPolling();
		}
	};

	useEffect(() => {
		if (!jobId) return;
		if (status === 'completed' || status === 'failed') return;

		stopPolling();
		pollTimerRef.current = setInterval(() => {
			pollStatus(jobId);
		}, 2000);

		// Poll immediately
		pollStatus(jobId);

		return () => stopPolling();
	}, [jobId]);

	const handleFileChange = (e) => {
		const files = Array.from(e.target.files || []);
		if (!files.length) return;

		// Only keep first 5 to match backend.
		setReferenceFiles((prev) => [...prev, ...files].slice(0, 5));
		e.target.value = '';
	};

	const removeFileAt = (idx) => {
		setReferenceFiles((prev) => prev.filter((_, i) => i !== idx));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError('');
		resetJob();

		const trimmed = prompt.trim();
		if (!trimmed) {
			setError('Please enter a prompt.');
			return;
		}

		let parsedDuration;
		if (duration !== '') {
			parsedDuration = Number(duration);
			const allowed = [4, 8, 12];
			if (!Number.isFinite(parsedDuration) || !allowed.includes(parsedDuration)) {
				setError('Please choose 4, 8, or 12 seconds.');
				return;
			}
		}

		setSubmitting(true);
		try {
			const formData = new FormData();
			formData.append('prompt', trimmed);
			if (parsedDuration !== undefined) {
				// API expects seconds; keep field name aligned with backend.
				formData.append('seconds', String(parsedDuration));
			}
			referenceFiles.forEach((file) => {
				formData.append('references', file);
			});

			const response = await fetch(`${API_BASE}/sora/prompt`, {
				method: 'POST',
				body: formData,
				credentials: 'include',
			});
			const data = await response.json();
			if (!response.ok) {
				throw new Error(data?.message || 'Failed to start video job');
			}

			setJobId(data.jobId || '');
			setStatus(data.status || 'queued');
			setProgress(Number(data.progress ?? 0));
		} catch (err) {
			setError(err?.message || 'Error starting video job.');
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<div>
			<Navbar />
			<div className="min-h-screen bg-gray-50 px-4 py-8">
				<div className="mx-auto w-full max-w-4xl">
					<div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
						<div className="flex items-start justify-between gap-4">
							<div>
								<h1 className="text-xl font-semibold text-gray-900">Sora Video Generator</h1>
								<p className="mt-1 text-sm text-gray-600">Enter a prompt and optionally add image/video references (up to 5).</p>
							</div>
							{(jobId || videoUrl) && (
								<button
									type="button"
									onClick={() => {
										setError('');
										resetJob();
										stopPolling();
									}}
									className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
								>
									New Video
								</button>
							)}
						</div>

						<form onSubmit={handleSubmit} className="mt-6 space-y-4">
							<div>
								<label className="block text-sm font-medium text-gray-700">Prompt</label>
								<textarea
									value={prompt}
									onChange={(e) => setPrompt(e.target.value)}
									rows={4}
									className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
									placeholder='e.g. "A cinematic product advert for a staffing platform, modern office, soft light, 8 seconds"'
								/>
							</div>

							<div className="grid gap-3 sm:grid-cols-2">
								<div>
									<label className="block text-sm font-medium text-gray-700">Seconds (optional)</label>
									<select
										value={duration}
										onChange={(e) => setDuration(e.target.value)}
										className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
									>
										<option value="">Select length</option>
										<option value="4">4 seconds</option>
										<option value="8">8 seconds</option>
										<option value="12">12 seconds</option>
									</select>
									<p className="mt-1 text-xs text-gray-500">Supported lengths: 4s, 8s, or 12s. Sora may still adjust slightly.</p>
								</div>
							</div>

							<div>
								<label className="block text-sm font-medium text-gray-700">References (optional)</label>
								<div className="mt-1 flex items-center gap-3">
									<input
										type="file"
										accept="image/*,video/*"
										multiple
										onChange={handleFileChange}
										disabled
										className="block w-full cursor-not-allowed text-sm text-gray-400 file:mr-4 file:rounded-md file:border-0 file:bg-gray-300 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-gray-600"
									/>
								</div>
								<p className="mt-1 text-xs text-gray-500">References are temporarily disabled; sora-2 currently accepts prompt-only.</p>
							</div>

							{previews.length > 0 && (
								<div className="rounded-md border border-gray-200 bg-gray-50 p-3">
									<p className="text-sm font-medium text-gray-700">Selected references</p>
									<div className="mt-3 grid gap-3 sm:grid-cols-2">
										{previews.map((p, idx) => (
											<div key={`${p.file.name}-${idx}`} className="rounded-md border border-gray-200 bg-white p-3">
												<div className="flex items-start justify-between gap-3">
													<div className="min-w-0">
														<p className="truncate text-sm font-medium text-gray-900" title={p.file.name}>{p.file.name}</p>
														<p className="text-xs text-gray-500">{formatBytes(p.file.size)} • {p.file.type || 'unknown'}</p>
													</div>
													<button
														type="button"
														onClick={() => removeFileAt(idx)}
														className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
													>
														Remove
													</button>
												</div>

												<div className="mt-3 overflow-hidden rounded-md bg-gray-100">
													{p.isImage ? (
														<img src={p.url} alt={p.file.name} className="h-40 w-full object-cover" />
													) : p.isVideo ? (
														<video src={p.url} controls className="h-40 w-full object-cover" />
													) : (
														<div className="flex h-40 items-center justify-center text-sm text-gray-600">Preview unavailable</div>
													)}
												</div>
											</div>
										))}
									</div>
								</div>
							)}

							{error && (
								<div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
							)}

							<div className="flex items-center gap-3">
								<button
									type="submit"
									disabled={submitting}
									className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
								>
									{submitting ? 'Starting…' : 'Generate Video'}
								</button>

								{jobId && (
									<div className="text-sm text-gray-600">
										<span className="font-medium text-gray-800">Job:</span> {jobId}
									</div>
								)}
							</div>
						</form>
					</div>

					{(jobId || videoUrl) && (
						<div className="mt-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
							<div className="flex items-start justify-between gap-4">
								<div>
									<h2 className="text-lg font-semibold text-gray-900">Output</h2>
									<p className="mt-1 text-sm text-gray-600">Status: <span className="font-medium text-gray-800">{status}</span></p>
								</div>
								{downloadUrl && (
									<a
										href={downloadUrl}
										className="rounded-md bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-black"
									>
										Download
									</a>
								)}
							</div>

							<div className="mt-4">
								<div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
									<div className="h-full bg-indigo-600" style={{ width: `${Math.min(100, Math.max(0, progress))}%` }} />
								</div>
								<p className="mt-2 text-xs text-gray-500">{Math.round(progress)}%</p>
							</div>

							<div className="mt-5 overflow-hidden rounded-lg bg-black">
								{videoUrl ? (
									<video src={videoUrl} controls className="w-full" />
								) : (
									<div className="flex h-64 items-center justify-center text-sm text-gray-200">Waiting for video…</div>
								)}
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

